/**
 * Naming_Flow message classifier.
 *
 * Pure function that classifies the End_User's plain-text reply during
 * the Naming_Flow as one of:
 *
 *   - `accepts`         — the user accepts the most-recently proposed name.
 *   - `requestsAnother` — the user wants a different suggestion.
 *   - `providesCustom`  — the user supplies a custom name (captured).
 *   - `ambiguous`       — none of the above; the AI must clarify.
 *
 * The classifier dispatches on the active conversation `language` and
 * tries matchers in the following priority order, returning on the
 * FIRST match:
 *
 *   1. `providesCustom` — checked first so phrases like
 *      "evet, adın Mira olsun" are captured as a custom name rather
 *      than swallowed by the broader "evet" accept pattern.
 *   2. `requestsAnother`
 *   3. `accepts`
 *   4. Default: `ambiguous`.
 *
 * Mixed-language input (e.g. the user replies in English while the
 * active language is Turkish) is intentionally NOT cross-tested: the
 * active language is the source of truth, so anything that does not
 * match its patterns falls through to `ambiguous`. This matches
 * Requirement 16.12's "ask one clarifying question" flow.
 *
 * The function is PURE — no IO, no `Date.now()`, no randomness — and
 * therefore composes cleanly with `reduceNaming` and the property
 * tests for Property 32.
 *
 * Validates: Requirements 16.4 (accept detection), 16.5 (request
 * another), 16.6 (custom-name capture), 16.12 (ambiguous fallback).
 * Supports Property 32 (Naming-flow reducer).
 */

import type { LanguageCode } from '../types/preferences';

/** Result of classifying a single user message during Naming_Flow. */
export type NamingClassification =
  | { kind: 'accepts' }
  | { kind: 'requestsAnother' }
  | { kind: 'providesCustom'; name: string }
  | { kind: 'ambiguous' };

// -----------------------------------------------------------------------------
// Per-language regex constants — exported so unit tests can target individual
// patterns without re-deriving them. Each pattern uses the `u` flag for
// Unicode-aware matching (so `\p{L}` works) and the `i` flag for case
// insensitivity (so "EVET" and "evet" both match).
// -----------------------------------------------------------------------------

// Turkish (`'tr'`)

/** Patterns whose capture group 1 holds a user-supplied custom name (TR). */
export const TR_PROVIDES_CUSTOM: readonly RegExp[] = [
  /(?:adın|ismin)\s+(\p{L}[\p{L}\p{M}\-']{0,40})\s+olsun/iu,
  /sana\s+(\p{L}[\p{L}\p{M}\-']{0,40})\s+(?:de|diyelim|diyeceğim)/iu,
  /seni\s+(\p{L}[\p{L}\p{M}\-']{0,40})\s+(?:diye\s+çağıracağım|olarak\s+çağırayım|olarak\s+çağıracağım)/iu,
  // "X olsun" — tek başına isim + olsun
  /^(\p{Lu}[\p{L}\p{M}\-']{1,40})\s+olsun[.!]?$/iu,
  // "adın X" veya "ismin X"
  /(?:adın|ismin)\s+(\p{Lu}[\p{L}\p{M}\-']{1,40})\b/iu,
  // "X derim sana" / "X diyeyim"
  /(\p{Lu}[\p{L}\p{M}\-']{1,40})\s+(?:derim|diyeyim|diyorum)\s+sana/iu,
];

/** "Suggest another name" patterns (TR). */
export const TR_REQUESTS_ANOTHER: RegExp =
  /\b(başka(?:sı)?|farklı|değiştir|önerme|yeni\s+(?:bir\s+)?isim|başka\s+(?:bir\s+)?isim)\b/iu;

/** Acceptance patterns (TR). */
export const TR_ACCEPTS: RegExp =
  /\b(evet|olur|tamam|kabul|beğendim|harika|güzel|çok\s+güzel|hoşuma\s+gitti|çok\s+güzel\s+isim|çok\s+güzel\s+bir\s+isim|tamamdır)\b/iu;

// English (`'en'`)

/** Patterns whose capture group 1 holds a user-supplied custom name (EN). */
export const EN_PROVIDES_CUSTOM: readonly RegExp[] = [
  /\bcall\s+yourself\s+(\p{L}[\p{L}\p{M}\-']{0,40})\b/iu,
  /\b(?:let'?s\s+call\s+you|how\s+about\s+i\s+call\s+you|i'?ll\s+call\s+you)\s+(\p{L}[\p{L}\p{M}\-']{0,40})\b/iu,
  /\byour\s+name\s+(?:is|will\s+be)\s+(\p{L}[\p{L}\p{M}\-']{0,40})\b/iu,
  // "X!" or "X." alone — single capitalized word as a name choice
  /^(\p{Lu}[\p{L}\p{M}\-']{1,40})[.!]?$/u,
  // "I'll name you X" / "name yourself X"
  /\b(?:name\s+(?:you|yourself))\s+(\p{Lu}[\p{L}\p{M}\-']{1,40})\b/iu,
];

/** "Suggest another name" patterns (EN). */
export const EN_REQUESTS_ANOTHER: RegExp =
  /\b(another|different|change|new\s+(?:one|name|suggestion)|something\s+else|try\s+again)\b/iu;

/** Acceptance patterns (EN). */
export const EN_ACCEPTS: RegExp =
  /\b(yes|sure|ok|okay|i\s+like\s+(?:it|that)|sounds\s+good|love\s+it|love\s+that|perfect|great|that('?s)?\s+(?:nice|cute|lovely))\b/iu;

// Greek (`'el'`)

/** Patterns whose capture group 1 holds a user-supplied custom name (EL). */
export const EL_PROVIDES_CUSTOM: readonly RegExp[] = [
  /(?:ας\s+σε\s+(?:λέω|λέμε)|θα\s+σε\s+λέω|σε\s+λέω)\s+(\p{L}[\p{L}\p{M}\-']{0,40})/iu,
  /(?:το\s+όνομά\s+σου|όνομά\s+σου)\s+(?:είναι|θα\s+είναι)\s+(\p{L}[\p{L}\p{M}\-']{0,40})/iu,
];

/** "Suggest another name" patterns (EL). */
export const EL_REQUESTS_ANOTHER: RegExp =
  /\b(άλλο|διαφορετικό|αλλαγή|καινούριο|μια\s+ακόμη)\b/iu;

/** Acceptance patterns (EL). */
export const EL_ACCEPTS: RegExp =
  /\b(ναι|εντάξει|μου\s+αρέσει|τέλεια|τέλειο|μ['ʹ’]?\s*αρέσει|ωραίο|όμορφο)\b/iu;

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

interface LanguagePatterns {
  readonly providesCustom: readonly RegExp[];
  readonly requestsAnother: RegExp;
  readonly accepts: RegExp;
}

const PATTERNS: Readonly<Record<LanguageCode, LanguagePatterns>> = {
  tr: {
    providesCustom: TR_PROVIDES_CUSTOM,
    requestsAnother: TR_REQUESTS_ANOTHER,
    accepts: TR_ACCEPTS,
  },
  en: {
    providesCustom: EN_PROVIDES_CUSTOM,
    requestsAnother: EN_REQUESTS_ANOTHER,
    accepts: EN_ACCEPTS,
  },
  el: {
    providesCustom: EL_PROVIDES_CUSTOM,
    requestsAnother: EL_REQUESTS_ANOTHER,
    accepts: EL_ACCEPTS,
  },
};

/** Strips trailing punctuation and surrounding whitespace from a captured name. */
const TRAILING_PUNCT = /[!?.,;:]+$/;
/** A captured name must contain at least one Unicode letter to be valid. */
const HAS_LETTER = /\p{L}/u;

/**
 * Normalizes a captured custom-name token. Returns the cleaned string,
 * or `null` if it fails the 1–40 character / "must contain a letter"
 * validation. Returning `null` lets the caller fall through to the
 * next matcher rather than emit a malformed `providesCustom`.
 */
function normalizeCapturedName(raw: string | undefined): string | null {
  if (raw === undefined) {
    return null;
  }
  const cleaned = raw.trim().replace(TRAILING_PUNCT, '').trim();
  if (cleaned.length < 1 || cleaned.length > 40) {
    return null;
  }
  if (!HAS_LETTER.test(cleaned)) {
    return null;
  }
  return cleaned;
}

/**
 * Tries every `providesCustom` pattern for the active language and
 * returns the first successfully captured + validated name. Returns
 * `null` when no pattern matches or every match fails validation.
 */
function tryProvidesCustom(
  message: string,
  patterns: readonly RegExp[],
): string | null {
  for (const pattern of patterns) {
    const match = pattern.exec(message);
    if (match === null) {
      continue;
    }
    const captured = match[1];
    const normalized = normalizeCapturedName(captured);
    if (normalized !== null) {
      return normalized;
    }
    // Match found but validation failed (e.g. capture missing letters);
    // continue probing remaining patterns before falling through.
  }
  return null;
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Classifies a Naming_Flow user reply against the active language's
 * matchers.
 *
 * Empty / whitespace-only messages short-circuit to `{ kind: 'ambiguous' }`
 * (there is nothing to classify).
 *
 * The function is total: every input returns a `NamingClassification`.
 *
 * @param args.message   The raw user message text (UTF-8).
 * @param args.language  The active conversation language (`'tr' | 'en' | 'el'`).
 */
export function classifyNamingResponse(args: {
  message: string;
  language: LanguageCode;
}): NamingClassification {
  const { message, language } = args;

  if (message.trim().length === 0) {
    return { kind: 'ambiguous' };
  }

  const patterns = PATTERNS[language];

  // 1. providesCustom (highest priority) — checked before generic
  //    accept patterns so "adın Mira olsun" doesn't get classified as
  //    a bare acceptance.
  const customName = tryProvidesCustom(message, patterns.providesCustom);
  if (customName !== null) {
    return { kind: 'providesCustom', name: customName };
  }

  // 2. requestsAnother
  if (patterns.requestsAnother.test(message)) {
    return { kind: 'requestsAnother' };
  }

  // 3. accepts
  if (patterns.accepts.test(message)) {
    return { kind: 'accepts' };
  }

  // 4. Default fallback (Requirement 16.12).
  return { kind: 'ambiguous' };
}

export default classifyNamingResponse;

// -----------------------------------------------------------------------------
// Rename-intent detector — works outside the Naming_Flow (any time)
// -----------------------------------------------------------------------------

/**
 * Patterns that signal the user wants to rename the AI companion.
 * Each pattern's capture group 1 holds the new name.
 *
 * Checked regardless of Naming_Flow phase so the user can rename at
 * any point in the conversation.
 */
const RENAME_PATTERNS: Readonly<Record<LanguageCode, readonly RegExp[]>> = {
  tr: [
    // "artık sana Mira diyeceğim / diyeceğiz"
    /artık\s+sana\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\s+diyeceğ/iu,
    // "sana Mira diyeceğim"
    /sana\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\s+diyeceğim/iu,
    // "adını Mira yap / olarak değiştir"
    /adın[ıi]\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\s+(?:yap|olarak\s+değiştir|değiştir)/iu,
    // "ismini Mira olarak değiştir"
    /ismini\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\s+(?:olarak\s+)?(?:değiştir|yap)/iu,
    // "bundan böyle adın Mira"
    /bundan\s+(?:böyle|sonra)\s+adın\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})/iu,
    // "seni Mira olarak çağıracağım"
    /seni\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\s+olarak\s+çağıracağım/iu,
  ],
  en: [
    // "I'll call you Mira / I'm going to call you Mira"
    /(?:i'?ll|i\s+will|i'?m\s+going\s+to)\s+call\s+you\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
    // "from now on your name is Mira"
    /from\s+now\s+on\s+(?:your\s+name\s+is|you'?re)\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
    // "change your name to Mira"
    /change\s+your\s+name\s+to\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
    // "rename yourself to Mira" / "rename you to Mira"
    /rename\s+(?:yourself?)\s+to\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
    // "your new name is Mira"
    /your\s+new\s+name\s+(?:is|will\s+be)\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
  ],
  el: [
    // "θα σε λέω Mira / από τώρα σε λέω Mira"
    /(?:θα\s+σε\s+λέω|από\s+τώρα\s+σε\s+λέω|σε\s+λέω)\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
    // "άλλαξε το όνομά σου σε Mira"
    /άλλαξε\s+(?:το\s+)?όνομά\s+σου\s+σε\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
    // "το νέο σου όνομα είναι Mira"
    /(?:το\s+)?νέο\s+σου\s+όνομα\s+(?:είναι|θα\s+είναι)\s+(\p{Lu}[\p{L}\p{M}\-']{1,39})\b/iu,
  ],
};

/**
 * Detects a rename intent in a free-form user message.
 *
 * Returns the new name string if a rename pattern matches, or `null`
 * if no rename intent is found. Works at any point in the conversation,
 * independent of the Naming_Flow phase.
 *
 * @param message  The raw user message text.
 * @param language The active conversation language.
 */
export function detectRenameIntent(
  message: string,
  language: LanguageCode,
): string | null {
  if (message.trim().length === 0) return null;
  const patterns = RENAME_PATTERNS[language];
  for (const pattern of patterns) {
    const match = pattern.exec(message);
    if (match === null) continue;
    const normalized = normalizeCapturedName(match[1]);
    if (normalized !== null) return normalized;
  }
  return null;
}
